import { apiClient } from './api/client';
// `import type` é apagado na compilação — não cria ciclo em runtime com draft.ts.
import type { CommitOnboardingPayload } from '../features/admin/onboarding/draft';
import type { FlowPermissionMap } from '@/types/document-comparison';

const API_BASE = '/companies';

export interface Company {
  id: number;
  name: string;
  client_email_domains?: string[];
  /** Teto de capabilities da empresa (vale pra todo vínculo nela). */
  capabilities?: Record<string, any>;
  /** @deprecated alias servido pelo backend até o PR 3; ler `capabilities`. */
  external_capabilities?: Record<string, any>;
  active?: boolean;
  allows_external_registration?: boolean;
  /** Teto de campos editáveis do processo (vale pra todo vínculo nela). */
  editable_fields?: string[];
}

export interface CreateCompanyPayload {
  name: string;
  client_email_domains?: string[];
  capabilities?: Record<string, any>;
  active?: boolean;
  allows_external_registration?: boolean;
  editable_fields?: string[];
}

export interface CnpjPattern {
  tipo: 'suffix' | 'root';
  valor: string;
}

/**
 * Despachante próprio da planta no SIGRA. Campo vazio herda o do
 * `sigra_config` do tipo de processo (ver backend `plant-despachante.ts`).
 */
export interface PlantSigraDespachante {
  despachante_responsavel_cnpj?: string;
  despachante_ponta_cnpj?: string;
  analista_despachante?: {
    aereo?: string;
    maritimo?: string;
    rodoviario?: string;
    todos?: string;
  };
}

export interface CreateCompanyPlantPayload {
  name: string;
  cnpj_patterns?: CnpjPattern[];
  /** `null` limpa o despachante da planta; ausente mantém o gravado. */
  sigra_despachante?: PlantSigraDespachante | null;
}

export interface IndustrialPlant {
  id: number;
  name: string;
  cnpj_patterns?: CnpjPattern[] | null;
  company_id?: number | null;
  sigra_despachante?: PlantSigraDespachante | null;
}

export interface AdoptablePlant {
  id: number;
  name: string;
  company_id: number | null;
}

export interface SubgroupCnpj {
  cnpj: string;
  grupo: string;
  cost_center_nome: string;
}

export interface OnboardingImpactByPlant {
  plantId: number;
  plantName: string;
  count: number;
}

export interface OnboardingImpactPreview {
  defaultProcessTypeId: number | null;
  plantIds: number[];
  total: number;
  byPlant: OnboardingImpactByPlant[];
}

export interface OnboardingResolveResult {
  updated: number;
  skippedAmbiguous: number;
  ambiguousCostCenters: { costCenterId: number | null; count: number }[];
}

export interface CompanyFull {
  id: number; name: string; active: boolean;
  client_email_domains: string[]; capabilities: Record<string, any>;
  /** @deprecated alias até o PR 3. */ external_capabilities?: Record<string, any>;
  allows_external_registration: boolean;
  editable_fields?: string[];
  plants_count: number; process_types_count: number;
  /** Vínculos ativos (user_company_roles) — inclui interno e externo. */
  memberships_count: number;
  /** Vínculos ativos de perfil 'cliente'. */
  external_employees_count: number;
}
/**
 * Configuração de leitura de uma empresa. `null` = herdar o centro de custo,
 * que é diferente de 'gemini'/`false` (o tipo decide que não).
 */
export interface ReadingFlags {
  ai_provider_invpl?: 'gemini' | 'local' | null;
  ai_provider_conhecimento?: 'gemini' | 'local' | null;
  ai_provider_classificacao?: 'gemini' | 'local' | null;
  ai_provider_comparacao?: 'gemini' | 'local' | null;
  ai_provider_po?: 'gemini' | 'local' | null;
  ai_provider_nf?: 'gemini' | 'local' | null;
  veloster_hold_off_window?: boolean | null;
  veloster_queue_priority?: 'baixa' | 'minima' | null;
}

export interface CompanyGraph {
  company: { id: number; name: string; active: boolean; client_email_domains: string[]; capabilities: Record<string, any>; /** @deprecated alias até o PR 3. */ external_capabilities?: Record<string, any>; allows_external_registration: boolean; editable_fields?: string[]; /** Chaves de leitura do cadastro (empresa sem tipo próprio). */ reading_flags?: Record<string, any> };
  plants: Array<{ id: number; name: string; cnpj_patterns: CnpjPattern[]; sigra_despachante: PlantSigraDespachante | null }>;
  process_types: Array<{ id: number; name: string; flow_config?: Record<string, any> | null; analysts: Array<{ id: number; name: string }> }>;
  /** Vínculos ativos (user_company_roles). */
  memberships_count: number;
  /** Pessoas distintas com vínculo ativo. */
  users_count: number;
  /** Vínculos ativos de perfil 'operacional'. */
  operacional_count: number;
  /** Vínculos ativos de perfil 'cliente'. */
  external_employees_count: number;
}

export const companiesService = {
  // Lista companies ativas (id/name) para o picker de acesso externo.
  async getAll(): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get<{ id: number; name: string }[]>(API_BASE);
    return response.data || [];
  },

  // Cria uma empresa (onboarding). Restrito a DevTools no backend.
  async create(payload: CreateCompanyPayload): Promise<Company> {
    const response = await apiClient.post<Company>(API_BASE, payload);
    return response.data;
  },

  // Atualiza caps/domínios de uma empresa (onboarding). Restrito a DevTools.
  async update(
    id: number,
    payload: Partial<CreateCompanyPayload>,
  ): Promise<Company> {
    const response = await apiClient.patch<Company>(`${API_BASE}/${id}`, payload);
    return response.data;
  },

  // Cria uma planta vinculada direto a uma company (onboarding). DevTools.
  async createPlant(
    companyId: number,
    payload: CreateCompanyPlantPayload,
  ): Promise<IndustrialPlant> {
    const response = await apiClient.post<IndustrialPlant>(
      `${API_BASE}/${companyId}/plants`,
      payload,
    );
    return response.data;
  },

  // Adota uma planta existente, vinculando-a a uma company. DevTools.
  async adoptPlant(
    companyId: number,
    plantId: number,
  ): Promise<IndustrialPlant> {
    const response = await apiClient.put<IndustrialPlant>(
      `${API_BASE}/${companyId}/plants/${plantId}/adopt`,
      {},
    );
    return response.data;
  },

  // Lista plantas candidatas a adoção (todas; front destaca órfãs). DevTools.
  async listAdoptablePlants(): Promise<AdoptablePlant[]> {
    const response = await apiClient.get<AdoptablePlant[]>(
      `${API_BASE}/meta/plants`,
    );
    return response.data || [];
  },

  // Catálogo de CNPJs distintos extraídos dos subgroups, para o combobox de
  // busca na criação de planta (onboarding). DevTools.
  async listSubgroupCnpjs(): Promise<SubgroupCnpj[]> {
    const response = await apiClient.get<SubgroupCnpj[]>(
      '/subgroups/cnpj-catalog',
    );
    return response.data || [];
  },

  // Retipa os processos default da empresa para o process_type da empresa.
  // Fill-not-clobber, idempotente. DevTools.
  async resolveProcesses(id: number): Promise<OnboardingResolveResult> {
    const response = await apiClient.post<OnboardingResolveResult>(
      `${API_BASE}/${id}/resolve-processes`,
      {},
    );
    return response.data;
  },

  async getAllFull(): Promise<CompanyFull[]> {
    const r = await apiClient.get<CompanyFull[]>(`${API_BASE}/all`);
    return r.data || [];
  },
  async updatePlant(companyId: number, plantId: number, payload: CreateCompanyPlantPayload): Promise<IndustrialPlant> {
    const r = await apiClient.patch<IndustrialPlant>(`${API_BASE}/${companyId}/plants/${plantId}`, payload);
    return r.data;
  },
  async removePlant(companyId: number, plantId: number): Promise<{ id: number; company_id: null }> {
    const r = await apiClient.delete<{ id: number; company_id: null }>(`${API_BASE}/${companyId}/plants/${plantId}`);
    return r.data;
  },
  // T7b: permissões de fluxo de comparação da EMPRESA (eram por CC).
  async getFlowPermissions(companyId: number): Promise<FlowPermissionMap> {
    const r = await apiClient.get<FlowPermissionMap>(
      `${API_BASE}/${companyId}/flow-permissions`,
    );
    return r.data ?? {};
  },
  async updateFlowPermissions(
    companyId: number,
    map: FlowPermissionMap,
  ): Promise<FlowPermissionMap> {
    const r = await apiClient.put<FlowPermissionMap>(
      `${API_BASE}/${companyId}/flow-permissions`,
      map,
    );
    return r.data;
  },

  /**
   * Configuração de leitura da empresa. O backend grava no `flow_config` dos
   * TIPOS de processo dela — a empresa não tem `features_config`. Campo com
   * `null` volta a herdar o default do catálogo; ausente não é tocado.
   */
  async updateReadingFlags(
    companyId: number,
    payload: ReadingFlags,
  ): Promise<{ updated: number; alvo?: 'tipos' | 'empresa' }> {
    const r = await apiClient.patch<{ updated: number; alvo?: 'tipos' | 'empresa' }>(
      `${API_BASE}/${companyId}/reading-flags`,
      payload,
    );
    return r.data;
  },
  async getGraph(companyId: number): Promise<CompanyGraph> {
    const r = await apiClient.get<CompanyGraph>(`${API_BASE}/${companyId}/graph`);
    return r.data;
  },

  // Commit do wizard de onboarding: cria tudo numa transação. DevTools.
  async commitOnboarding(payload: CommitOnboardingPayload): Promise<{
    company_id: number;
    plant_ids: number[];
    process_type_ids: number[];
    agent_id: number | null;
    resolve: OnboardingResolveResult | null;
    resolve_error: string | null;
  }> {
    const r = await apiClient.post(`${API_BASE}/onboarding`, payload);
    return r.data;
  },

  // Impacto do retype para uma lista de plantas (wizard, antes de criar). DevTools.
  async onboardingImpactForPlants(
    plantIds: number[],
  ): Promise<OnboardingImpactPreview> {
    const r = await apiClient.post<OnboardingImpactPreview>(
      `${API_BASE}/onboarding-impact`,
      { plant_ids: plantIds },
    );
    return r.data;
  },
};
